import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTextMessaging } from '../../../../models/hooks/appStateHooks';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { FileSendLinkPlaceholder, FileRequestLinkPlaceholder } from '../../../../models/Token';
import { debounce } from '../../../../models/UiUtils';
import { brandPrimaryHover, navigation, oldSilver } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { Checkbox } from '../../../components-v2/Checkbox';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { AttachIcon } from '../../svgs/icons/AttachIcon';
import { DoubleArrowIcon } from '../../svgs/icons/DoubleArrowIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { EmojiPickerButton } from '../EmojiPickerButton';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { styleSheet } from './styles';
import { TextMessageComposer, type ITextMessageComposerHandle } from './TextMessageComposer';

interface IProps extends IEventLoggingComponentProps {
	/**
	 * @deprecated Use styles instead
	 */
	className?: string;
	styles?: StyleDeclarationValue[];
	conversation?: Api.ConversationViewModel;
	onSend?(
		msg: string,
		attachments: Api.AttachmentsToBeUploadedViewModel<File>,
		sendAttachmentsAsLink: boolean
	): Promise<void>;
	hideTextSend?: boolean;
	canSendFileLinks?: boolean;
	canRequestFiles?: boolean;
}

const allFileLinkSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];

const TextMessageInputBase: React.FC<IProps> = ({
	className = '',
	onSend,
	logApiError,
	conversation,
	hideTextSend,
	styles = [],
	canSendFileLinks,
	canRequestFiles,
}) => {
	const textMessageVm = useTextMessaging();
	const [message, setMessage] = useState('');
	const [showingSizeLimitWarning, setShowingSizeLimitWarning] = useState(false);
	const [showingFileLinkWarning, setShowingFileLinkWarning] = useState(false);
	const [sendAttachmentsAsLink, setSendAttachmentsAsLink] = useState(false);
	const [, forceUpdate] = useReducer(x => x + 1, 0);
	const attachmentsToBeSent = useRef<Api.AttachmentsToBeUploadedViewModel<File>>(
		new Api.AttachmentsToBeUploadedViewModel(null, Api.TextMessagingMaxFileByteSize)
	).current;
	const filesImportRef = useRef<HTMLInputElement>(null);
	const composerRef = useRef<ITextMessageComposerHandle | null>(null);
	const [isSending, setIsSending] = useState(false);

	const totalAttachmentBytes = attachmentsToBeSent.attachments.reduce(
		(sum: number, f: Api.FileWithExtensionsType) => sum + f.size,
		0
	);
	const sendingNumber =
		textMessageVm.getPhoneNumberOrderById(conversation?.phoneNumberId) ?? textMessageVm.phoneNumberOrder;
	const providerLimits = Api.getTextingProviderLimits(sendingNumber?.provider);
	const maxDirectAttachmentBytes = providerLimits.maxAttachmentBytes;
	const isTotalSizeLarge = totalAttachmentBytes > maxDirectAttachmentBytes;

	useEffect(() => {
		setMessage('');
		attachmentsToBeSent.remove(attachmentsToBeSent.attachments);
		setSendAttachmentsAsLink(false);
	}, [setMessage, attachmentsToBeSent]);

	useEffect(() => {
		if (isTotalSizeLarge && !sendAttachmentsAsLink && canSendFileLinks) {
			setSendAttachmentsAsLink(true);
			composerRef.current?.insertToken(FileSendLinkPlaceholder.symbol, 'Send Files');
		}
		// sendAttachmentsAsLink omitted: re-running while already true would duplicate the token.
		// canSendFileLinks omitted: stable for the session lifetime (derived from account features).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTotalSizeLarge]);

	const updateTypingindicator = React.useMemo(
		() =>
			debounce(() => {
				if (conversation?.id) {
					textMessageVm?.updateTypingIndicator(conversation.id)?.catch(() => {
						// eat this error
					});
				}
			}, 100),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation?.id]
	);

	const onAttachClick = React.useCallback(() => {
		filesImportRef?.current?.click();
	}, []);

	const onEmojiClick = React.useCallback((_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
		composerRef.current?.insertText(data.emoji);
	}, []);

	const onRemoveImage = (i: number) => () => {
		const nextFiles = attachmentsToBeSent.attachments.filter((_, index) => index !== i);
		attachmentsToBeSent.attachments.forEach((x: Api.FileWithExtensionsType, idx) => {
			if (!!x.url && idx === i) {
				URL.revokeObjectURL(x.url);
			}
		});
		attachmentsToBeSent.remove(attachmentsToBeSent.attachments);
		attachmentsToBeSent.add(Array.from(nextFiles));

		if (nextFiles.length === 0 && sendAttachmentsAsLink) {
			setSendAttachmentsAsLink(false);
			composerRef.current?.removeTokensBySymbols(allFileLinkSymbols);
		}
	};

	const handleToggleSendAsLink = useCallback(
		(checked: boolean) => {
			setSendAttachmentsAsLink(checked);
			if (checked) {
				const hasToken = allFileLinkSymbols.some(s => message.includes(s));
				if (!hasToken) {
					composerRef.current?.insertToken(FileSendLinkPlaceholder.symbol, 'Send Files');
				}
			} else {
				composerRef.current?.removeTokensBySymbols(allFileLinkSymbols);
			}
		},
		[message]
	);

	const addFilesToAttachments = React.useCallback(
		(files: FileList) => {
			attachmentsToBeSent.add(Array.from(files));
			if (attachmentsToBeSent.attachments.length) {
				attachmentsToBeSent.attachments.forEach((x: Api.FileWithExtensionsType) => {
					const url = URL.createObjectURL(x);
					x.url = url;
					forceUpdate();
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[attachmentsToBeSent]
	);

	const onFilesInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length) {
			addFilesToAttachments(e.target.files);

			e.target.value = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onFilesInputRef = React.useCallback((ref?: HTMLInputElement) => {
		filesImportRef.current = ref;
	}, []);

	const onSendMessageClick = React.useCallback(async () => {
		if (hideTextSend || isSending) {
			return;
		}

		if (message.trim() || attachmentsToBeSent.attachments.length) {
			const hasFileLinkToken = allFileLinkSymbols.some(s => message.includes(s));
			if (hasFileLinkToken && attachmentsToBeSent.attachments.length === 0) {
				setShowingFileLinkWarning(true);
				return;
			}
			if (providerLimits.maxCharacters != null && message.trim().length > providerLimits.maxCharacters) {
				toast.error(
					`Texts sent through ${providerLimits.providerDisplayName} are limited to ${providerLimits.maxCharacters} characters.`
				);
				return;
			}
			if (
				providerLimits.allowedAttachmentMimeTypes &&
				attachmentsToBeSent.attachments.some(
					(f: Api.FileWithExtensionsType) => !providerLimits.allowedAttachmentMimeTypes.includes(f.type?.toLowerCase())
				)
			) {
				toast.error(`${providerLimits.providerDisplayName} only supports JPG, PNG, GIF, and PDF attachments.`);
				return;
			}
			const maxBytes = sendAttachmentsAsLink ? Api.TextMessagingMaxLinkFileByteSize : maxDirectAttachmentBytes;
			if (totalAttachmentBytes > maxBytes) {
				setShowingSizeLimitWarning(true);
				return;
			}
			setIsSending(true);
			try {
				await onSend?.(message, attachmentsToBeSent, sendAttachmentsAsLink);
				attachmentsToBeSent.attachments.forEach((x: Api.FileWithExtensionsType) => {
					if (x.url) {
						URL.revokeObjectURL(x.url);
					}
				});
				attachmentsToBeSent.remove(attachmentsToBeSent.attachments);
				setSendAttachmentsAsLink(false);
				composerRef.current?.reset();
				setMessage('');
			} catch (error) {
				logApiError('SendMessage-Error', Api.asApiError(error));
			} finally {
				setIsSending(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSending, message, attachmentsToBeSent, totalAttachmentBytes, sendAttachmentsAsLink, onSend, setMessage]);

	const onCloseSizeWarning = () => {
		setShowingSizeLimitWarning(false);
	};

	const onCloseFileLinkWarning = () => {
		setShowingFileLinkWarning(false);
	};

	const hiddenInputStyle = React.useRef<React.CSSProperties>({
		height: 0,
		opacity: 0,
		pointerEvents: 'none',
		position: 'fixed',
		visibility: 'hidden',
		width: 0,
		zIndex: -1,
	}).current;

	const composerTokens = [
		canSendFileLinks ? { symbol: FileSendLinkPlaceholder.symbol, text: 'Send Files' } : null,
		canRequestFiles ? { symbol: FileRequestLinkPlaceholder.symbol, text: 'File Request' } : null,
	].filter((t): t is { symbol: string; text: string } => t !== null);

	return (
		<div className={`${css(styleSheet.container, ...styles)} ${className}`}>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.toolbar)}>
				<div className={css(styleSheet.toolbarButtonsContainer)}>
					<EmojiPickerButton onEmojiClick={onEmojiClick} />
					<button className={css(styleSheet.transparentBtn)} onClick={onAttachClick}>
						<AttachIcon fillColor={navigation} />
					</button>
				</div>
				{(canSendFileLinks || canRequestFiles) && (
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.fileLinksTrigger]}>File Links</DropdownSelectTrigger>
						<DropdownContent align='start' sideOffset={4} styles={[styleSheet.fileLinksContent]}>
							{canRequestFiles && (
								<DropdownItem
									onClick={() => composerRef.current?.insertToken(FileRequestLinkPlaceholder.symbol, 'File Request')}
								>
									File Request Link
								</DropdownItem>
							)}
							{canSendFileLinks && (
								<DropdownItem onClick={() => handleToggleSendAsLink(true)}>File Download Link</DropdownItem>
							)}
						</DropdownContent>
					</Dropdown>
				)}
			</div>
			{attachmentsToBeSent?.attachments?.length > 0 && (
				<div className={css(styleSheet.imageAreaContainer)}>
					<div className={css(styleSheet.attachmentPreviewsRow)}>
						{attachmentsToBeSent.attachments.map((file: Api.FileWithExtensionsType, i) => (
							<div className={css(styleSheet.previewContainer)} key={i}>
								{Api.VmUtils?.imageFileExtensions.find(e => file?.type?.includes(e)) ? (
									<div className={css(styleSheet.imageArea)}>
										<img className={css(styleSheet.image)} src={file.url} alt={file.name} />
										<DeprecatedCloseButton
											className={css(styleSheet.closeButton)}
											fillColor={oldSilver}
											onClick={onRemoveImage(i)}
											opacity={0.9}
											outlineColor='#FFF'
											outlineWidth={1.5}
											widthAndHeight={16}
											xMark={true}
										/>
									</div>
								) : (
									<div className={css(styleSheet.fileArea)}>
										<span className={css(styleSheet.fileContainer)}>{file.name}</span>
										<DeprecatedCloseButton
											className={css(styleSheet.closeButton)}
											fillColor={oldSilver}
											onClick={onRemoveImage(i)}
											opacity={0.9}
											outlineColor='#FFF'
											outlineWidth={1.5}
											widthAndHeight={16}
											xMark={false}
										/>
									</div>
								)}
							</div>
						))}
					</div>

					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{
							isOpen: !!showingSizeLimitWarning,
							onRequestClose: onCloseSizeWarning,
						}}
						title='Max File Size Exceeded'
					>
						<p className={css(styleSheet.sizeWarning)}>
							{sendAttachmentsAsLink
								? `Reduce your total file size below ${Api.convertToMB(Api.TextMessagingMaxLinkFileByteSize / Api.ONE_KB)} and resend.`
								: `Reduce your total file size below ${Api.convertToMB(maxDirectAttachmentBytes / Api.ONE_KB)} and resend.`}
						</p>
					</ConfirmationDialog>

					{canSendFileLinks && (
						<div className={css(styleSheet.sendAsLinkRow)}>
							<Checkbox
								id='text-message-send-as-link'
								checked={isTotalSizeLarge || sendAttachmentsAsLink}
								disabled={isTotalSizeLarge}
								onCheckedChange={handleToggleSendAsLink}
							/>
							<label className={css(styleSheet.sendAsLinkLabel)} htmlFor='text-message-send-as-link'>
								{isTotalSizeLarge ? 'Send as download link (required for large files)' : 'Send as download link'}
							</label>
						</div>
					)}
					{sendAttachmentsAsLink && !allFileLinkSymbols.some(s => message.includes(s)) && (
						<p className={css(styleSheet.sendAsLinkWarning)}>{'Add a {{send files}} merge field to your message.'}</p>
					)}
				</div>
			)}
			<div
				className={css(
					styleSheet.textAreaContainer,
					attachmentsToBeSent?.attachments?.length > 0 && styleSheet.textAreaWithImages
				)}
			>
				<TextMessageComposer
					ref={composerRef}
					tokens={composerTokens}
					placeholder='Enter message'
					autoFocus={true}
					onChange={text => {
						setMessage(text);
						updateTypingindicator();
					}}
					onSend={onSendMessageClick}
					onFilePaste={addFilesToAttachments}
				/>
				{!hideTextSend ? (
					<button
						className={css(styleSheet.transparentBtn, styleSheet.sendMessageBtn)}
						disabled={isSending}
						onClick={onSendMessageClick}
					>
						<DoubleArrowIcon fillColor={message.length > 0 ? brandPrimaryHover : navigation} />
					</button>
				) : null}
			</div>
			<input
				id='text-message-files-input'
				multiple={true}
				onChange={onFilesInputChanged}
				ref={onFilesInputRef}
				style={hiddenInputStyle}
				type='file'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showingFileLinkWarning,
					onRequestClose: onCloseFileLinkWarning,
				}}
				title='Attachment Required'
			>
				<p className={css(styleSheet.sizeWarning)}>
					{
						'Your message contains a {{send files}} merge field but no files are attached. Please attach a file or remove the merge field before sending.'
					}
				</p>
			</ConfirmationDialog>
		</div>
	);
};

export const TextMessageInput = withEventLogging(observer(TextMessageInputBase), 'TextMessageInput');
